import React from 'react'

import type { DynamicCopyElement } from '../types'

export const LOCALES = {
  EN_US: 'en-us',
  EN_CA: 'en-ca',
  FR: 'fr',
  FR_CA: 'fr-ca',
  ES: 'es',
}

export const VALID_LOCALES = Object.values(LOCALES)

export const getLocale = () => {
  const localeDefault = LOCALES.EN_US

  const localeParam = (window as any)?.app?.options?.language?.toLowerCase() ?? ''
  const localeBrowser = window.navigator.language.toLowerCase()

  if (localeParam === 'browser' && VALID_LOCALES.includes(localeBrowser)) {
    return localeBrowser
  } else if (VALID_LOCALES.includes(localeParam)) {
    return localeParam
  } else {
    return localeDefault
  }
}

// matches a pattern like "static text _1{category_name} and  _2{amount} more static text"
const copyPattern = /(?<static>.*?)(?<dynamic>(?:_\d+{(?:.*?)}))|(?<staticEnd>.+)/g

export const buildDynamicCopy = (str: string, ...args: DynamicCopyElement[]) => {
  if (!str) return ''

  const hasFormattedElement = args.some((arg) => typeof arg !== 'string')

  // finds all instances of matching pattern in given string
  const regexMatches = [...str.matchAll(copyPattern)]
  const matches = regexMatches.reduce(
    (acc: DynamicCopyElement[], match: RegExpMatchArray, matchIndex) => {
      const temp = [...acc]
      const groups = match.groups
      if (groups?.static) {
        temp.push(groups.static)
      }

      if (groups?.dynamic) {
        const curlyIndex = groups.dynamic.indexOf('{')
        const variableName = groups.dynamic.substring(curlyIndex + 1, groups.dynamic.length - 1)
        const index = Number(groups.dynamic.substring(1, curlyIndex))
        if (React.isValidElement(args[index - 1])) {
          const element = args[index - 1] as React.ReactElement
          const clonedElement = React.cloneElement(element, {
            key: `${variableName}-${matchIndex}`,
          })
          temp.push(clonedElement)
        } else {
          temp.push(args[index - 1])
        }
      }

      if (groups?.staticEnd) {
        temp.push(groups.staticEnd)
      }

      return temp
    },
    [],
  )

  return hasFormattedElement ? (matches as DynamicCopyElement[]) : (matches.join('') as string)
}

export const buildPluralCopy = (
  strOne: string,
  strMulti: string,
  count: number,
  ...args: DynamicCopyElement[]
) => {
  if (!strOne || !strMulti) return ''
  const str = count === 1 ? strOne : strMulti

  return buildDynamicCopy(str, ...args)
}
